import { runInAction, makeAutoObservable, action } from 'mobx';
import { Auth, AuthLoginResponseProps, languageToCode, Profile, ProfileType } from '@ixicare/resources';

import { State } from '~/store/types';
import UserProfileEntry from './Profile/UserProfileEntry';
import * as Sentry from '@sentry/react';
import TokenService, { IMPERSONATION_ORIGINAL_KEY, migrateLegacyKey } from '~services/TokenService';
import PersistentStoreService from '~services/PersistentStoreService';
import { queryClient } from '~services/queryClient';
import { hasStoredLanguage, setLanguage } from '~/i18n';

class AuthStore {
  rootStore: State;
  error: Error | null = null;
  userType?: ProfileType;

  isLoggedIn: boolean = false;
  loading: boolean = true;
  user?: UserProfileEntry | null = null;
  isInitialized = false;

  originalAuthResponse: AuthLoginResponseProps | null = null;
  impersonationExpiresAt: Date | null = null;

  constructor(rootStore: State) {
    makeAutoObservable(this, {
      init: action.bound,
      logout: action.bound,
      login: action.bound,
      loginAs: action.bound,
      exitLoginAs: action.bound,
      setTokenData: action.bound,
      fetchUser: action.bound,
      rootStore: false,
    });

    this.rootStore = rootStore;
  }

  get isImpersonating(): boolean {
    return this.originalAuthResponse !== null;
  }

  init() {
    const stashedOriginal =
      PersistentStoreService.get<AuthLoginResponseProps>(IMPERSONATION_ORIGINAL_KEY) ??
      migrateLegacyKey<AuthLoginResponseProps>(IMPERSONATION_ORIGINAL_KEY);
    TokenService.getAuthResponse()
      .then((parsed) => {
        if (parsed && new Date(parsed.expiryDate) > new Date()) {
          if (stashedOriginal) {
            runInAction(() => {
              this.originalAuthResponse = stashedOriginal;
              this.impersonationExpiresAt = new Date(parsed.expiryDate);
            });
          }
          void this.setTokenData(parsed);
        } else {
          this.logout();
        }
      })
      .catch((e) => {
        console.error(e);
        this.logout();
      })
      .finally(() => {
        runInAction(() => {
          this.loading = false;
          this.isInitialized = true;
        });
      });
  }

  async logout() {
    const user = this.user;

    this.rootStore.reset();

    this.isLoggedIn = false;
    this.isInitialized = true;
    this.user = undefined;
    this.userType = undefined;
    this.originalAuthResponse = null;
    this.impersonationExpiresAt = null;

    if (user) {
      Auth.logout().then(console.info).catch(console.error);
    }
    try {
      await Promise.all([TokenService.clear(), PersistentStoreService.clear()]);
    } catch (err) {
      console.error('logout -> clear services', err);
    }
  }

  async login(username: string, password: string): Promise<boolean> {
    this.loading = true;
    try {
      const response = await Auth.login({ email: username, password: password });
      const allowedTypes = [ProfileType.HealthcareProvider, ProfileType.Installer, ProfileType.WatcherrEmployee];
      if (allowedTypes.includes(response.profileType)) {
        await this.setTokenData(response);
        return true;
      }
      runInAction(() => {
        this.loading = false;
        this.error = new Error('Did not include profile type');
      });
      return false;
    } catch (error) {
      runInAction(() => {
        this.loading = false;
        this.error = error as Error;
      });
      return false;
    }
  }

  async setTokenData(data: AuthLoginResponseProps): Promise<void> {
    TokenService.setAuthResponse(data);
    this.userType = data.profileType;
    this.user = null;
    this.rootStore.permissions.loadPermissions();
    this.isInitialized = true;
    this.isLoggedIn = true;
    // Prime own presence so SoundService reflects backend silence state from
    // load 1, rather than waiting on the next self_presence_changed event.
    void this.rootStore.presence.refreshOwn();
    await this.fetchUser(data.profileId);
    runInAction(() => {
      this.loading = false;
    });
  }

  async loginAs(profileId: number): Promise<boolean> {
    if (this.isImpersonating) return false;
    try {
      const original = await TokenService.getAuthResponse();
      const response = await Auth.startImpersonation({ profileId });
      PersistentStoreService.set(IMPERSONATION_ORIGINAL_KEY, original);
      runInAction(() => {
        this.originalAuthResponse = original;
        this.impersonationExpiresAt = new Date(response.expiryDate);
      });
      this.rootStore.reset();
      queryClient.clear();
      await this.setTokenData(response);
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
      return false;
    }
  }

  async exitLoginAs(): Promise<void> {
    const original = this.originalAuthResponse;
    if (!original) return;
    try {
      await Auth.leaveImpersonation();
    } catch (e) {
      console.warn('exitLoginAs: backend call failed, restoring original session anyway', e);
    }
    PersistentStoreService.remove(IMPERSONATION_ORIGINAL_KEY);
    runInAction(() => {
      this.originalAuthResponse = null;
      this.impersonationExpiresAt = null;
    });
    this.rootStore.reset();
    queryClient.clear();
    await this.setTokenData(original);
  }

  async fetchUser(profileId: number): Promise<void> {
    try {
      const data = await Profile.getProfile(profileId);
      const userEntry = new UserProfileEntry(this.rootStore, data);
      runInAction(() => {
        this.user = userEntry;
        Sentry.setUser({ id: data.uuid });
      });
      // localStorage is the source of truth — only seed from the fetched
      // profile when this device has no stored choice yet (fresh browser,
      // post-logout, etc.). Otherwise leave the local pick alone.
      if (!hasStoredLanguage() && data.language) {
        setLanguage(languageToCode(data.language));
      }
    } catch (e) {
      console.error(e);
      this.logout();
    }
  }
}

export default AuthStore;
